/**
 * NMS Explore More Drawer
 *
 * Drawer opened when clicking a collection link inside the
 * "Explore More" block. Uses a bottom-sheet grid on mobile and a
 * centered modal with scroll-carousel on tablet/desktop.
 *
 * The target collection is fetched through:
 *
 *   /collections/<handle>?view=explore-more-drawer
 *
 * Required Shopify files:
 *
 *   templates/collection.explore-more-drawer.json
 *   sections/nms-explore-more-drawer-products.liquid
 *
 * The fetched response must contain:
 *
 *   [data-nms-explore-more-drawer-products]
 *
 * This component extends the theme's existing `x-modal` custom element and
 * overrides its enter/leave animations: bottom sheet on mobile, centered
 * modal with fade + scale on tablet/desktop.
 */

(() => {
  'use strict';

  const DRAWER_TAG_NAME = 'nms-explore-more-drawer';
  const BASE_MODAL_TAG_NAME = 'x-modal';

  const TRIGGER_SELECTOR =
    '[data-nms-explore-more-drawer-trigger]';

  const ROOT_SELECTOR =
    '[data-nms-explore-more]';

  const PRODUCTS_CONTAINER_SELECTOR =
    '[data-nms-explore-more-drawer-products]';

  const DEFAULT_PRODUCT_LIMIT = 10;
  const DEFAULT_HEADING = 'Explore More';

  /**
   * Convert a supplied value into a safe product limit.
   *
   * @param {string|number|null|undefined} value
   * @returns {number}
   */
  function normalizeProductLimit(value) {
    const parsedValue = Number.parseInt(value, 10);

    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return DEFAULT_PRODUCT_LIMIT;
    }

    return parsedValue;
  }

  /**
   * Return the visible text used for the drawer title.
   *
   * Priority:
   * 1. data-drawer-title
   * 2. Explore More title inside the clicked item
   * 3. Link aria-label
   * 4. Default heading
   *
   * @param {HTMLElement} trigger
   * @returns {string}
   */
  function getTriggerHeading(trigger) {
    const explicitTitle =
      trigger.dataset.drawerTitle?.trim();

    if (explicitTitle) {
      return explicitTitle;
    }

    const itemTitle = trigger.querySelector(
      '.nms-explore-more__title'
    )?.textContent?.trim();

    if (itemTitle) {
      return itemTitle.replace(/\s+/g, ' ');
    }

    const ariaLabel =
      trigger.getAttribute('aria-label')?.trim();

    if (ariaLabel) {
      return ariaLabel;
    }

    return DEFAULT_HEADING;
  }

  /**
   * Register the custom Explore More drawer after x-modal is available.
   */
  function registerExploreMoreDrawer() {
    if (window.customElements.get(DRAWER_TAG_NAME)) {
      return;
    }

    const ModalBaseClass =
      window.customElements.get(BASE_MODAL_TAG_NAME);

    if (!ModalBaseClass) {
      console.error(
        `NMS Explore More Drawer: base "${BASE_MODAL_TAG_NAME}" custom element is unavailable.`
      );

      return;
    }

    class NMSExploreMoreDrawer extends ModalBaseClass {
      #contentCache = new Map();
      #pendingFetches = new Map();
      #activeRequestId = null;
      #activeTrigger = null;
      #hasScheduledPrefetch = false;

      connectedCallback() {
        if (typeof super.connectedCallback === 'function') {
          super.connectedCallback();
        }

        this.addEventListener('dialog:before-show', this.#compensateScrollbar);
        this.addEventListener('dialog:after-hide', this.#restoreScrollbar);

        // Close this drawer once a product inside it is added to the cart
        this.addEventListener('variant:add', this.#handleVariantAdd, { signal: this.abortController?.signal });

        this.#ensureLayout();
        this.#schedulePrefetch();
      }

      /**
       * Prefetch the collection markup for every visible Explore More trigger
       * once the browser is idle, so the drawer opens instantly when clicked.
       */
      #schedulePrefetch() {
        if (this.#hasScheduledPrefetch) {
          return;
        }

        this.#hasScheduledPrefetch = true;

        const root = this.closest(ROOT_SELECTOR);

        if (!root) {
          return;
        }

        const prefetch = () => {
          const connection = navigator.connection;

          if (
            connection?.saveData ||
            connection?.effectiveType === '2g'
          ) {
            return;
          }

          const limit =
            root.dataset.productsLimit ||
            DEFAULT_PRODUCT_LIMIT;

          const triggers = Array.from(
            root.querySelectorAll(TRIGGER_SELECTOR)
          );

          triggers.forEach((trigger) => {
            const href =
              trigger.getAttribute('href');

            if (
              !href ||
              href === '#' ||
              href.startsWith('javascript:') ||
              trigger.dataset.nmsExploreMorePrefetchRequested
            ) {
              return;
            }

            trigger.dataset.nmsExploreMorePrefetchRequested =
              'true';

            this.prefetchExploreMore(href, limit);
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(prefetch, {
            timeout: 1500
          });
        } else {
          setTimeout(prefetch, 1000);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('dialog:before-show', this.#compensateScrollbar);
        this.removeEventListener('dialog:after-hide', this.#restoreScrollbar);
        this.#restoreScrollbar();

        if (typeof super.disconnectedCallback === 'function') {
          super.disconnectedCallback();
        }
      }

      /**
       * Prevent the drawer from closing when a size popup is open above it.
       */
      hideForOutsideClickTarget(target) {
        if (window.neemans?.sizePopupOpen) {
          return false;
        }

        return super.hideForOutsideClickTarget ? super.hideForOutsideClickTarget(target) : !this.contains(target);
      }

      #handleVariantAdd(event) {
        // Only close if the added product came from inside this drawer
        if (this.contains(event.target)) {
          this.hide();
        }
      }

      #compensateScrollbar() {
        // .lock adds overflow:hidden to <html>, which clips background
        // content behind the modal. Override with inline styles so the
        // html stays visible while body scroll is locked instead.
        document.documentElement.style.overflow = 'visible';
        document.body.style.overflow = 'hidden';
      }

      #restoreScrollbar() {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }

      /**
       * Animate as a bottom sheet on mobile and a centered modal on tablet/desktop.
       */
      createEnterAnimationControls() {
        return this.#isMobileBreakpoint()
          ? this.#animateBottomSheet('enter')
          : this.#animateModal('enter');
      }

      /**
       * Animate as a bottom sheet on mobile and a centered modal on tablet/desktop.
       */
      createLeaveAnimationControls() {
        return this.#isMobileBreakpoint()
          ? this.#animateBottomSheet('leave')
          : this.#animateModal('leave');
      }

      /**
       * Check whether the current viewport is mobile.
       *
       * @returns {boolean}
       */
      #isMobileBreakpoint() {
        return window.matchMedia(
          'screen and (max-width: 767px)'
        ).matches;
      }

      /**
       * Create a centered modal animation.
       *
       * @param {'enter'|'leave'} direction
       * @returns {{finished: Promise<void>, finish: Function}}
       */
      #animateModal(direction) {
        const duration = 300;

        const easing =
          'cubic-bezier(0.645, 0.045, 0.355, 1)';

        const overlay =
          this.getShadowPartByName?.('overlay');

        const content =
          this.getShadowPartByName?.('content');

        const isEntering = direction === 'enter';

        const startOpacity = isEntering ? '0' : '1';
        const endOpacity = isEntering ? '1' : '0';

        const startScale = isEntering ? '0.95' : '1';
        const endScale = isEntering ? '1' : '0.95';

        if (!overlay || !content) {
          return {
            finished: Promise.resolve(),

            finish() {
              // No-op fallback.
            }
          };
        }

        overlay.style.transition = 'none';
        content.style.transition = 'none';

        // Reset any bottom-sheet inline layout styles that may have been
        // applied when the drawer was previously opened on mobile.
        content.style.width = '';
        content.style.maxWidth = '';
        content.style.marginInline = '';
        content.style.marginBlockStart = '';

        overlay.style.opacity = startOpacity;
        content.style.transform = `scale(${startScale})`;

        // Force layout so the browser registers the initial state.
        void content.offsetHeight;

        overlay.style.transition =
          `opacity ${duration}ms ${easing}`;

        content.style.transition =
          `transform ${duration}ms ${easing}, opacity ${duration}ms ${easing}`;

        overlay.style.opacity = endOpacity;
        content.style.transform = `scale(${endScale})`;
        content.style.opacity = endOpacity;

        let isFinished = false;
        let resolveFinished;

        const finished = new Promise((resolve) => {
          resolveFinished = resolve;
        });

        const finishAnimation = () => {
          if (isFinished) {
            return;
          }

          isFinished = true;

          window.clearTimeout(timeoutId);

          overlay.style.transition = 'none';
          content.style.transition = 'none';

          overlay.style.opacity = endOpacity;

          // Clear transform to avoid trapping fixed-position descendants.
          content.style.transform =
            isEntering ? '' : `scale(${endScale})`;
          content.style.opacity = endOpacity;

          resolveFinished();
        };

        const timeoutId = window.setTimeout(
          finishAnimation,
          duration
        );

        return {
          finished,
          finish: finishAnimation
        };
      }

      /**
       * Create a bottom-sheet animation for mobile.
       *
       * @param {'enter'|'leave'} direction
       * @returns {{finished: Promise<void>, finish: Function}}
       */
      #animateBottomSheet(direction) {
        const duration = 300;

        const easing =
          'cubic-bezier(0.645, 0.045, 0.355, 1)';

        const overlay =
          this.getShadowPartByName?.('overlay');

        const content =
          this.getShadowPartByName?.('content');

        const isEntering = direction === 'enter';

        const startOpacity = isEntering ? '0' : '1';
        const endOpacity = isEntering ? '1' : '0';

        const startTransform = isEntering
          ? 'translateY(100%)'
          : 'translateY(0)';

        const endTransform = isEntering
          ? 'translateY(0)'
          : 'translateY(100%)';

        if (!overlay || !content) {
          return {
            finished: Promise.resolve(),

            finish() {
              // No-op fallback.
            }
          };
        }

        overlay.style.transition = 'none';
        content.style.transition = 'none';

        overlay.style.opacity = startOpacity;
        content.style.transform = startTransform;
        content.style.opacity = startOpacity;

        // Force the drawer to behave as a bottom sheet.
        content.style.width = '100%';
        content.style.maxWidth = '100%';
        content.style.marginInline = '0';
        content.style.marginBlockStart = 'auto';

        // Force layout so the browser registers the initial state.
        void content.offsetHeight;

        overlay.style.transition =
          `opacity ${duration}ms ${easing}`;

        content.style.transition =
          `transform ${duration}ms ${easing}, opacity ${duration}ms ${easing}`;

        overlay.style.opacity = endOpacity;
        content.style.transform = endTransform;
        content.style.opacity = endOpacity;

        let isFinished = false;
        let resolveFinished;

        const finished = new Promise((resolve) => {
          resolveFinished = resolve;
        });

        const finishAnimation = () => {
          if (isFinished) {
            return;
          }

          isFinished = true;

          window.clearTimeout(timeoutId);

          overlay.style.transition = 'none';
          content.style.transition = 'none';

          overlay.style.opacity = endOpacity;

          // Clear transform to avoid trapping fixed-position descendants.
          content.style.transform =
            isEntering ? '' : endTransform;
          content.style.opacity = endOpacity;

          resolveFinished();
        };

        const timeoutId = window.setTimeout(
          finishAnimation,
          duration
        );

        return {
          finished,
          finish: finishAnimation
        };
      }

      /**
       * Ensure the light-DOM content expected by x-modal exists.
       */
      #ensureLayout() {
        let headerContainer = this.querySelector(
          '[data-nms-explore-more-drawer-header-container]'
        );

        if (!headerContainer) {
          headerContainer = document.createElement('div');

          headerContainer.className =
            'nms-explore-more-drawer__header';
          headerContainer.setAttribute('slot', 'header');

          headerContainer.setAttribute(
            'data-nms-explore-more-drawer-header-container',
            ''
          );

          const heading = document.createElement('p');
          heading.className = 'h3 nms-explore-more-drawer__heading';
          heading.setAttribute(
            'data-nms-explore-more-drawer-header',
            ''
          );
          heading.textContent =
            this.getAttribute('heading') ||
            DEFAULT_HEADING;

          const subtitle = document.createElement('p');
          subtitle.className = 'nms-explore-more-drawer__subtitle';
          subtitle.setAttribute(
            'data-nms-explore-more-drawer-subtitle',
            ''
          );
          subtitle.textContent = '';

          headerContainer.appendChild(heading);
          headerContainer.appendChild(subtitle);

          this.appendChild(headerContainer);
        }

        let body = this.querySelector(
          '[data-nms-explore-more-drawer-body]'
        );

        if (!body) {
          body = document.createElement('div');

          body.className =
            'nms-explore-more-drawer__body';

          body.setAttribute(
            'data-nms-explore-more-drawer-body',
            ''
          );

          body.innerHTML = this.#getLoadingMarkup();

          this.appendChild(body);
        }
      }

      /**
       * Update the drawer heading.
       *
       * @param {string} heading
       */
      #setHeading(heading) {
        const header = this.querySelector(
          '[data-nms-explore-more-drawer-header]'
        );

        if (!header) {
          return;
        }

        header.textContent =
          heading?.trim() ||
          this.getAttribute('heading') ||
          DEFAULT_HEADING;
      }

      /**
       * Update the drawer subtitle (e.g. "Showing 10 product results").
       *
       * @param {number} shownCount
       * @param {number} totalCount
       */
      #setSubtitle(shownCount, totalCount) {
        const subtitle = this.querySelector(
          '[data-nms-explore-more-drawer-subtitle]'
        );

        if (!subtitle) {
          return;
        }

        if (shownCount > 0) {
          subtitle.textContent = `Showing ${shownCount} product results`;
        } else {
          subtitle.textContent = '';
        }
      }

      /**
       * Open the underlying x-modal.
       *
       * @returns {boolean}
       */
      #showModal() {
        if (typeof this.show === 'function') {
          this.show();
          return true;
        }

        /*
         * This fallback is included in case the current theme version exposes
         * showModal() instead of show().
         */
        if (typeof this.showModal === 'function') {
          this.showModal();
          return true;
        }

        console.error(
          'NMS Explore More Drawer: the base x-modal component does not expose show() or showModal().'
        );

        return false;
      }

      /**
       * Build the collection fetch URL while preserving existing query
       * parameters such as filter.v.price.lte.
       *
       * @param {string} url
       * @returns {URL}
       */
      #buildFetchUrl(url) {
        const fetchUrl = new URL(
          url,
          window.location.origin
        );

        fetchUrl.searchParams.delete('view');
        fetchUrl.searchParams.set(
          'section_id',
          'nms-explore-more-drawer-products'
        );

        return fetchUrl;
      }

      /**
       * Return the loading state markup.
       *
       * @returns {string}
       */
      #getLoadingMarkup() {
        return `
          <div
            class="nms-explore-more-drawer__loading"
            role="status"
            aria-live="polite"
          >
            Loading…
          </div>
        `;
      }

      /**
       * Return an error-state message.
       *
       * @returns {string}
       */
      #getErrorMarkup() {
        return `
          <p
            class="nms-explore-more-drawer__error"
            role="alert"
          >
            Sorry, we could not load these products right now.
          </p>
        `;
      }

      /**
       * Build the markup placed inside the drawer.
       *
       * @param {HTMLElement} productsContainer
       * @param {number} numericLimit
       * @param {string} viewAllUrl
       * @returns {string}
       */
      #buildDrawerMarkup(
        productsContainer,
        numericLimit,
        viewAllUrl
      ) {
        const sourceItems = Array.from(
          productsContainer.children
        );

        const currentProductHandle =
          this.dataset.currentProductHandle || '';

        let filteredItems = sourceItems;

        if (currentProductHandle) {
          const matchIndex = sourceItems.findIndex(
            (item) =>
              item.dataset.productHandle ===
              currentProductHandle
          );

          if (matchIndex >= 0) {
            filteredItems = sourceItems.slice();
            filteredItems.splice(matchIndex, 1);
          }
        }

        const selectedItems =
          filteredItems.slice(0, numericLimit);

        if (selectedItems.length === 0) {
          throw new Error(
            'The Explore More products container did not include any product items.'
          );
        }

        const rawTotalCount =
          parseInt(
            productsContainer.dataset.totalProductsCount,
            10
          ) || sourceItems.length;

        const excludedCount =
          sourceItems.length - filteredItems.length;

        const totalCount = Math.max(
          0,
          rawTotalCount - excludedCount
        );

        const collectionTitle =
          productsContainer.dataset.collectionTitle || '';

        const shownCount = selectedItems.length;

        const carouselId =
          'nms-explore-more-carousel-' +
          Date.now() +
          '-' +
          Math.random().toString(36).slice(2, 8);

        const wrapper =
          document.createElement('div');

        wrapper.className =
          'nms-explore-more-drawer__results';

        /* --- Mobile layout: grid + sticky view-all button --- */
        const mobileLayout =
          document.createElement('div');
        mobileLayout.className =
          'nms-explore-more-drawer__layout nms-explore-more-drawer__layout--mobile';

        const mobileGrid =
          document.createElement('div');
        mobileGrid.className =
          'nms-explore-more-drawer-products';

        selectedItems.forEach((item) => {
          mobileGrid.appendChild(item.cloneNode(true));
        });

        const mobileViewAllContainer =
          document.createElement('div');
        mobileViewAllContainer.className =
          'nms-explore-more-drawer__view-all-container';

        const mobileViewAllLink =
          document.createElement('a');
        mobileViewAllLink.className =
          'button nms-button nms-button--large nms-button--primary nms-explore-more-drawer__view-all';
        mobileViewAllLink.href = viewAllUrl;
        mobileViewAllLink.innerHTML =
          '<span>View all ' + totalCount + ' Styles</span>' +
          '<span class="nms-button__icon">' +
            '<svg aria-hidden="true" focusable="false" fill="none" width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="Caret Right">' +
              '<path d="M6 3L11 8L6 13" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</span>';

        mobileViewAllContainer.appendChild(
          mobileViewAllLink
        );

        mobileLayout.appendChild(mobileGrid);

        if (totalCount > numericLimit) {
          mobileLayout.appendChild(mobileViewAllContainer);
        }

        wrapper.appendChild(mobileLayout);

        /* --- Desktop/Tablet layout: carousel + bottom nav --- */
        const desktopLayout =
          document.createElement('div');
        desktopLayout.className =
          'nms-explore-more-drawer__layout nms-explore-more-drawer__layout--desktop';

        /* Scroll carousel */
        const carousel =
          document.createElement('scroll-carousel');
        carousel.id = carouselId;
        carousel.setAttribute('group-cells', '');
        carousel.setAttribute('allow-drag', '');
        carousel.className =
          'nms-explore-more-drawer__carousel scroll-area';

        selectedItems.forEach((item) => {
          const cell = document.createElement('div');
          cell.className =
            'nms-explore-more-drawer__carousel-cell';
          cell.appendChild(item.cloneNode(true));
          carousel.appendChild(cell);
        });

        /* "View all" promo card inside the carousel */
        const promoCell =
          document.createElement('div');
        promoCell.className =
          'nms-explore-more-drawer__carousel-cell nms-explore-more-drawer__promo-cell';
        promoCell.innerHTML =
          '<div class="nms-explore-more-drawer__promo-card">' +
            '<p class="nms-explore-more-drawer__promo-title h3">Explore the Complete Collection</p>' +
            '<p class="nms-explore-more-drawer__promo-text">' +
              (collectionTitle
                ? `View every ${collectionTitle} style in one place.`
                : 'View every style in one place.') +
            '</p>' +
            '<a href="' + viewAllUrl + '" class="button nms-button nms-button--large nms-button--primary nms-explore-more-drawer__promo-button">' +
              '<span>View all ' + totalCount + ' Styles</span>' +
              '<span class="nms-button__icon">' +
                '<svg aria-hidden="true" focusable="false" fill="none" width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="Caret Right">' +
                  '<path d="M6 3L11 8L6 13" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</span>' +
            '</a>' +
          '</div>';

        if (totalCount > numericLimit) {
          carousel.appendChild(promoCell);
        }

        desktopLayout.appendChild(carousel);

        /* Bottom navigation: prev / dots / next */
        const bottomNav =
          document.createElement('div');
        bottomNav.className =
          'nms-explore-more-drawer__bottom-nav';
        bottomNav.dataset.productCount =
          String(selectedItems.length);

        const prevButton =
          document.createElement('carousel-prev-button');
        prevButton.setAttribute('aria-controls', carouselId);
        prevButton.className =
          'nms-explore-more-drawer__nav-button nms-explore-more-drawer__nav-button--prev';
        prevButton.innerHTML =
          '<button type="button" class="nms-button nms-button--secondary nms-button--large nms-button--icon-only" aria-label="Previous" disabled>' +
            '<span class="nms-button__icon">' +
              '<svg aria-hidden="true" focusable="false" fill="none" width="20" viewBox="0 0 25 24" role="img" aria-label="Caret Left">' +
                '<path d="M15.2339 19.7146L7.73389 12.2146L15.2339 4.7146" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</span>' +
            '<span class="sr-only">Previous</span>' +
          '</button>';

        const cellCount = selectedItems.length + 1;

        const navigation =
          document.createElement('div');
        navigation.setAttribute('data-nms-explore-more-drawer-dots', '');
        navigation.setAttribute('data-carousel-id', carouselId);
        navigation.setAttribute('data-cell-count', String(cellCount));
        navigation.className =
          'nms-explore-more-drawer__page-dots';
        navigation.setAttribute('role', 'tablist');
        navigation.setAttribute('aria-label', 'Carousel pages');

        const nextButton =
          document.createElement('carousel-next-button');
        nextButton.setAttribute('aria-controls', carouselId);
        nextButton.className =
          'nms-explore-more-drawer__nav-button nms-explore-more-drawer__nav-button--next';
        nextButton.innerHTML =
          '<button type="button" class="nms-button nms-button--secondary nms-button--large nms-button--icon-only" aria-label="Next">' +
            '<span class="nms-button__icon">' +
              '<svg aria-hidden="true" focusable="false" fill="none" width="20" viewBox="0 0 25 24" role="img" aria-label="Caret Right">' +
                '<path d="M9.5 4.5L17 12L9.5 19.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</span>' +
            '<span class="sr-only">Next</span>' +
          '</button>';

        bottomNav.appendChild(prevButton);
        bottomNav.appendChild(navigation);
        bottomNav.appendChild(nextButton);

        desktopLayout.appendChild(bottomNav);

        wrapper.appendChild(desktopLayout);

        return {
          html: wrapper.outerHTML,
          shownCount,
          totalCount
        };
      }

      /**
       * Build HTML for custom page dots (one dot per visible group).
       *
       * @param {number} cellCount
       * @param {number} visiblePerPage
       * @returns {string}
       */
      #buildPageDotsHtml(cellCount, visiblePerPage) {
        const pageCount = Math.max(
          1,
          Math.ceil(cellCount / visiblePerPage)
        );
        let html = '';

        for (let i = 0; i < pageCount; i++) {
          const isActive = i === 0;
          const targetCell = i * visiblePerPage;
          html +=
            '<button type="button" class="tap-area" role="tab" aria-selected="' +
            (isActive ? 'true' : 'false') +
            '" aria-label="Go to page ' +
            (i + 1) +
            '" data-target-cell="' +
            targetCell +
            '">' +
              '<span class="sr-only">Page ' +
              (i + 1) +
              '</span>' +
            '</button>';
        }

        return html;
      }

      /**
       * Wire up the custom page dots to the carousel.
       * Clicking a dot scrolls to its target cell; the carousel's
       * carousel:change event keeps the active dot in sync.
       *
       * @param {HTMLElement} body
       */
      #connectPageDots(body) {
        const dotsContainer = body.querySelector(
          '[data-nms-explore-more-drawer-dots]'
        );

        if (!dotsContainer) {
          return;
        }

        const carouselId =
          dotsContainer.dataset.carouselId;
        const carousel =
          document.getElementById(carouselId);

        if (!carousel) {
          return;
        }

        const bottomNav =
          dotsContainer.parentElement;
        const productCount =
          parseInt(
            bottomNav?.dataset.productCount,
            10
          ) || 0;

        const cellCount = parseInt(
          dotsContainer.dataset.cellCount,
          10
        );

        if (Number.isNaN(cellCount) || cellCount <= 0) {
          return;
        }

        const renderDots = () => {
          const visiblePerPage = Math.max(
            1,
            carousel.groupCells || 1
          );
          dotsContainer.innerHTML = this.#buildPageDotsHtml(
            cellCount,
            visiblePerPage
          );
          updateDots(carousel.selectedIndex || 0);

          if (bottomNav && productCount > 0) {
            bottomNav.hidden =
              productCount <= visiblePerPage;
          }
        };

        const updateDots = (selectedCellIndex) => {
          const visiblePerPage = Math.max(
            1,
            carousel.groupCells || 1
          );
          const activePage = Math.floor(
            selectedCellIndex / visiblePerPage
          );
          const dots = Array.from(
            dotsContainer.querySelectorAll('button')
          );

          dots.forEach((dot, index) => {
            dot.setAttribute(
              'aria-selected',
              index === activePage ? 'true' : 'false'
            );
          });
        };

        dotsContainer.addEventListener('click', (event) => {
          const dot = event.target.closest('button');

          if (!dot) {
            return;
          }

          const targetCell = parseInt(
            dot.dataset.targetCell,
            10
          );

          if (!Number.isNaN(targetCell)) {
            carousel.select(targetCell, { instant: false });
          }
        });

        carousel.addEventListener(
          'carousel:change',
          (event) => {
            updateDots(event.detail.index);
          }
        );

        // Render dots with the correct count for the current viewport.
        renderDots();

        // Re-render when the carousel becomes visible or the breakpoint changes.
        const resizeObserver = new ResizeObserver(() => {
          if (carousel.offsetWidth > 0) {
            renderDots();
          }
        });

        resizeObserver.observe(carousel);
      }

      /**
       * Fetch and parse the Explore More section using the Section Rendering API.
       *
       * @param {URL} fetchUrl
       * @param {number} numericLimit
       * @param {string} viewAllUrl
       * @returns {Promise<{html: string, shownCount: number, totalCount: number}>}
       */
      async #fetchProductMarkup(
        fetchUrl,
        numericLimit,
        viewAllUrl,
        options = {}
      ) {
        const response = await fetch(
          fetchUrl.toString(),
          {
            method: 'GET',

            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            },

            priority: options.priority
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${response.statusText} ` +
            `(requested ${fetchUrl.toString()})`
          );
        }

        const html = await response.text();

        const tempDocument =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const productsContainer =
          tempDocument.querySelector(
            PRODUCTS_CONTAINER_SELECTOR
          );

        if (!productsContainer) {
          console.warn(
            'NMS Explore More Drawer: response did not include the expected product container.',
            {
              expectedSelector:
                PRODUCTS_CONTAINER_SELECTOR,

              requestedUrl:
                fetchUrl.toString(),

              finalUrl:
                response.url,

              redirected:
                response.redirected,

              status:
                response.status
            }
          );

          throw new Error(
            `Could not find ${PRODUCTS_CONTAINER_SELECTOR} ` +
            `in the fetched response.`
          );
        }

        return this.#buildDrawerMarkup(
          productsContainer,
          numericLimit,
          viewAllUrl
        );
      }

      /**
       * Return cached markup for a URL/limit, or fetch and cache it.
       *
       * Pending fetches are deduplicated so that concurrent hover/click events
       * for the same URL share a single network request.
       *
       * @param {URL} fetchUrl
       * @param {number} numericLimit
       * @param {string} viewAllUrl
       * @param {object} options
       * @returns {Promise<{html: string, shownCount: number, totalCount: number}>}
       */
      async #getProductMarkup(
        fetchUrl,
        numericLimit,
        viewAllUrl,
        options = {}
      ) {
        const cacheKey =
          `${fetchUrl.toString()}::${numericLimit}`;

        const cached =
          this.#contentCache.get(cacheKey);

        if (cached) {
          return cached;
        }

        const pending =
          this.#pendingFetches.get(cacheKey);

        if (pending) {
          return pending;
        }

        const fetchPromise = this.#fetchProductMarkup(
          fetchUrl,
          numericLimit,
          viewAllUrl,
          options
        ).then((markup) => {
          this.#contentCache.set(cacheKey, markup);
          this.#pendingFetches.delete(cacheKey);
          return markup;
        }).catch((error) => {
          this.#pendingFetches.delete(cacheKey);
          throw error;
        });

        this.#pendingFetches.set(
          cacheKey,
          fetchPromise
        );

        return fetchPromise;
      }

      /**
       * Update the subtitle after content is loaded.
       *
       * @param {number} shownCount
       * @param {number} totalCount
       */
      #updateSubtitle(shownCount, totalCount) {
        this.#setSubtitle(shownCount, totalCount);
      }

      /**
       * Notify the theme that dynamically loaded product cards have been
       * inserted. Other scripts may listen for this event to initialize
       * quick-add, product-card swatches, size selectors, sliders, etc.
       *
       * @param {HTMLElement} body
       * @param {string} url
       */
      #dispatchProductsLoadedEvent(body, url) {
        /*
         * Product cards fetched via AJAX are not covered by the theme's
         * DOMContentLoaded auto-init pass, so they need to be initialized
         * manually. This is what wires up the size-selection popup (and
         * add-to-cart, swatches, etc.) so it behaves identically to product
         * cards rendered elsewhere in the theme (e.g. main-collection.liquid).
         */
        if (typeof window.initNMSProductCard === 'function') {
          window.initNMSProductCard();
        }

        document.dispatchEvent(
          new CustomEvent(
            'nms:explore-more-products-loaded',
            {
              detail: {
                drawer: this,
                container: body,
                url
              }
            }
          )
        );
      }

      /**
       * Prefetch product markup for a trigger so the drawer opens instantly.
       *
       * @param {string} url
       * @param {string|number} limit
       */
      prefetchExploreMore(url, limit) {
        if (
          !url ||
          url === '#' ||
          url.startsWith('javascript:')
        ) {
          return;
        }

        const numericLimit =
          normalizeProductLimit(limit);

        try {
          const originalUrl = new URL(
            url,
            window.location.origin
          );

          const fetchUrl = this.#buildFetchUrl(url);

          originalUrl.searchParams.delete('view');
          originalUrl.searchParams.delete(
            'section_id'
          );

          const viewAllUrl =
            `${originalUrl.pathname}${originalUrl.search}${originalUrl.hash}`;

          this.#getProductMarkup(
            fetchUrl,
            numericLimit,
            viewAllUrl,
            { priority: 'low' }
          ).catch(() => {
            // Prefetch failures are silent.
          });
        } catch (error) {
          // Invalid URL; ignore.
        }
      }

      /**
       * Fetch a collection and open it inside the modal drawer.
       *
       * This method is intentionally not named `open()` to avoid colliding
       * with any inherited method from the theme's x-modal component.
       *
       * @param {string} url
       * @param {string|number} limit
       * @param {string} heading
       * @param {HTMLElement|null} trigger
       */
      async openExploreMore(
        url,
        limit,
        heading = DEFAULT_HEADING,
        trigger = null
      ) {
        if (!url) {
          console.error(
            'NMS Explore More Drawer: no collection URL was supplied.'
          );

          return;
        }

        this.#ensureLayout();
        this.#setHeading(heading);
        this.#setSubtitle(0, 0);

        this.#activeTrigger = trigger;

        const body = this.querySelector(
          '[data-nms-explore-more-drawer-body]'
        );

        if (!body) {
          console.error(
            'NMS Explore More Drawer: body container could not be created.'
          );

          return;
        }

        const numericLimit =
          normalizeProductLimit(limit);

        body.innerHTML =
          this.#getLoadingMarkup();

        const didOpen = this.#showModal();

        if (!didOpen) {
          return;
        }

        const requestId = Symbol();
        this.#activeRequestId = requestId;

        try {
          const originalUrl = new URL(
            url,
            window.location.origin
          );

          const fetchUrl = this.#buildFetchUrl(url);

          /*
           * Do not include drawer-only query params in the user-facing
           * View All URL.
           */
          originalUrl.searchParams.delete('view');
          originalUrl.searchParams.delete(
            'section_id'
          );

          const viewAllUrl =
            `${originalUrl.pathname}${originalUrl.search}${originalUrl.hash}`;

          const markup =
            await this.#getProductMarkup(
              fetchUrl,
              numericLimit,
              viewAllUrl,
              { priority: 'high' }
            );

          if (this.#activeRequestId !== requestId) {
            return;
          }

          body.innerHTML = markup.html;

          this.#updateSubtitle(
            markup.shownCount,
            markup.totalCount
          );

          this.#connectPageDots(body);

          this.#dispatchProductsLoadedEvent(
            body,
            viewAllUrl
          );
        } catch (error) {
          if (this.#activeRequestId !== requestId) {
            return;
          }

          console.error(
            'NMS Explore More Drawer: failed to load products.',
            error
          );

          body.innerHTML =
            this.#getErrorMarkup();
        }
      }
    }

    window.customElements.define(
      DRAWER_TAG_NAME,
      NMSExploreMoreDrawer
    );
  }

  /**
   * Register immediately when x-modal is already available; otherwise wait
   * until theme.js defines it.
   */
  if (window.customElements.get(BASE_MODAL_TAG_NAME)) {
    registerExploreMoreDrawer();
  } else {
    window.customElements
      .whenDefined(BASE_MODAL_TAG_NAME)
      .then(registerExploreMoreDrawer)
      .catch((error) => {
        console.error(
          'NMS Explore More Drawer: failed while waiting for x-modal.',
          error
        );
      });
  }

  /**
   * Click delegation
   *
   * This listener is attached once and works with blocks added dynamically by
   * Shopify's Theme Editor.
   */
  if (
    !window.__nmsExploreMoreDrawerListenerAdded
  ) {
    window.__nmsExploreMoreDrawerListenerAdded =
      true;

    const prefetchTrigger = async (trigger) => {
      const href = trigger.getAttribute('href');

      if (
        !href ||
        href === '#' ||
        href.startsWith('javascript:') ||
        trigger.dataset.nmsExploreMorePrefetchRequested
      ) {
        return;
      }

      const root = trigger.closest(ROOT_SELECTOR);

      if (!root) {
        return;
      }

      let drawer;

      try {
        await window.customElements.whenDefined(
          DRAWER_TAG_NAME
        );

        drawer = root.querySelector(
          DRAWER_TAG_NAME
        );
      } catch (error) {
        return;
      }

      if (
        !drawer ||
        typeof drawer.prefetchExploreMore !==
          'function'
      ) {
        return;
      }

      trigger.dataset.nmsExploreMorePrefetchRequested =
        'true';

      const productLimit =
        root.dataset.productsLimit ||
        DEFAULT_PRODUCT_LIMIT;

      drawer.prefetchExploreMore(href, productLimit);
    };

    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType === 'touch') {
          return;
        }

        const trigger = event.target.closest(
          TRIGGER_SELECTOR
        );

        if (!trigger) {
          return;
        }

        prefetchTrigger(trigger);
      },
      { passive: true }
    );

    document.addEventListener(
      'focusin',
      (event) => {
        const trigger = event.target.closest(
          TRIGGER_SELECTOR
        );

        if (!trigger) {
          return;
        }

        prefetchTrigger(trigger);
      }
    );

    document.addEventListener(
      'click',
      async (event) => {
        const eventTarget = event.target;

        if (!(eventTarget instanceof Element)) {
          return;
        }

        const trigger = eventTarget.closest(
          TRIGGER_SELECTOR
        );

        if (!trigger) {
          return;
        }

        /*
         * Ignore modified clicks so users can still open the collection in a
         * new tab using Ctrl/Cmd/Shift/Alt or the middle mouse button.
         */
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const href =
          trigger.getAttribute('href');

        if (
          !href ||
          href === '#' ||
          href.startsWith('javascript:')
        ) {
          return;
        }

        const root = trigger.closest(
          ROOT_SELECTOR
        );

        if (!root) {
          console.warn(
            'NMS Explore More Drawer: trigger is not inside [data-nms-explore-more].',
            trigger
          );

          return;
        }

        /*
         * Wait for the drawer class in case the user clicks immediately after
         * page load, before theme.js finishes registering x-modal.
         */
        try {
          await window.customElements.whenDefined(
            DRAWER_TAG_NAME
          );
        } catch (error) {
          console.error(
            'NMS Explore More Drawer: custom element could not be initialized.',
            error
          );

          return;
        }

        const drawer = root.querySelector(
          DRAWER_TAG_NAME
        );

        if (
          !drawer ||
          typeof drawer.openExploreMore !==
            'function'
        ) {
          /*
           * Do not prevent navigation. The collection link remains a working
           * fallback when the drawer component is unavailable.
           */
          console.warn(
            'NMS Explore More Drawer: drawer element or openExploreMore() method is unavailable.'
          );

          return;
        }

        event.preventDefault();

        const productLimit =
          root.dataset.productsLimit ||
          DEFAULT_PRODUCT_LIMIT;

        const heading =
          getTriggerHeading(trigger);

        drawer.openExploreMore(
          href,
          productLimit,
          heading,
          trigger
        );
      }
    );
  }
})();